import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import parse from 'html-react-parser';
import axios from 'axios';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import { devices } from 'styles/devices';
import { colors } from 'styles/colors';
import SidebarTemplate from 'templates/SidebarTemplate/SidebarTemplate';
import Loader from 'components/Loader';
import Image from 'components/Image';
import Headline from 'ui/Headline';

import Files from './Files';
import Section from '../../components/Section';
import { BoxImage, GridImage } from '../../components/BoxImage';

const NewsView = () => {
   const params = useParams();
   const [content, setContent] = useState(null);
   const id = params?.newsId || params?.instructionId;
   const [loading, setLoading] = useState(true);
   const intl = useIntl();

   const getData = async (postId) => {
      const { data: post } = await axios.get(`/wp/v2/posts/${postId}`);
      let featuredMedia = '';

      if (post.featured_media) {
         const { data: media } = await axios.get(`/wp/v2/media/${post.featured_media}`);
         featuredMedia = media ? media.source_url : '';
      }

      setContent({
         title: post.title.rendered,
         content: post.content.rendered,
         publishedAt: dayjs(post.date).format('DD-MM-YYYY'),
         featuredMedia,
         files:
            (post.acf.files || [])?.map(({ file }) => ({
               name: file.filename,
               url: file.url
            })) || [],
         informacje: post?.acf?.informacje,
         akcesoria: post?.acf?.akcesoria
      });

      setLoading(false);
   };

   useEffect(() => {
      setLoading(true);
      setContent(null);
      getData(id);
   }, [id]);

   return (
      <SidebarTemplate navigation="news">
         {loading ? (
            <LoaderWrapper>
               <Loader />
            </LoaderWrapper>
         ) : (
            <>
               {content && (
                  <InnerWrapper>
                     <Headline size="XXL">{parse(content.title)}</Headline>
                     <Small>
                        <FormattedMessage id="PublicationDate" /> {content.publishedAt}
                     </Small>
                     <StyledImage src={content.featuredMedia} />
                     <IntroWrapper>{parse(content.content || '')}</IntroWrapper>
                     {!!content.informacje && typeof content.informacje === 'string' && (
                        <Section
                           id="informacje"
                           headline={intl.formatMessage({
                              id: 'NewsView.Information'
                           })}
                        >
                           {parse(content.informacje || '', { trim: true })}
                        </Section>
                     )}
                     {!!content.akcesoria?.length && (
                        <Section
                           id="akcesoria"
                           headline={intl.formatMessage({
                              id: 'NewsView.Accessories'
                           })}
                        >
                           {!!content.akcesoria_opis && (
                              <div>{parse(content.akcesoria_opis, { trim: true })}</div>
                           )}
                           <GridImage>
                              {content.akcesoria?.map((item) => (
                                 <BoxImage
                                    key={Math.random() + item?.akcesoria_img?.ID}
                                    image={item?.akcesoria_img?.url || ''}
                                    headline={item?.akcesoria_description || ''}
                                 />
                              ))}
                           </GridImage>
                        </Section>
                     )}
                     <Files files={content?.files || []} />
                  </InnerWrapper>
               )}
            </>
         )}
      </SidebarTemplate>
   );
};

const LoaderWrapper = styled.div`
   height: 20vh;
`;

const Small = styled.small`
   font-size: 14px;
   margin-top: 8px;
   display: block;
`;

const StyledImage = styled(Image)`
   margin-top: 20px;

   ${devices.tablet} {
      margin: 20px auto 0;
   }

   ${devices.desktopHD} {
      width: 70%;
   }

   ${devices.desktopFHD} {
      width: 66%;
   }
`;

const IntroWrapper = styled.div`
   max-width: 640px;

   ${devices.tabletXL} {
      margin-top: 20px;
   }

   ${devices.desktopHD} {
      margin-top: 30px;
   }

   ${devices.desktopFHD} {
      margin-top: 40px;
   }

   h1,
   h2,
   h3,
   h4,
   h5,
   h6 {
      display: block;
      font-weight: 700;
      line-height: 1.3;
      margin: 40px 0 20px;
   }

   h1,
   h2 {
      font-size: 29px;
   }

   h3 {
      font-size: 27px;
   }

   h4 {
      font-size: 25px;
   }

   h5 {
      font-size: 23px;
   }

   h6 {
      font-size: 21px;
   }

   p {
      font-size: 19px;
      line-height: 1.5;
      margin-bottom: 12px;
      justify-content: justify;
   }

   table {
      width: 100% !important;
   }

   figure {
      margin: 40px 0;

      > img {
         width: 100%;
         height: auto;
      }

      > figcaption {
         font-size: 14px;
         text-align: center;
         margin: 20px auto 0;
         max-width: 400px;
      }
   }

   iframe {
      width: 100%;
      height: 360px;
      margin: 40px 0 0;
   }

   video {
      width: 100%;
      height: auto;
      margin: 40px 0 0;
   }

   hr {
      display: block;
      margin: 40px 0;
   }

   a {
      color: ${colors.primary};
      text-decoration: underline;
      &:hover {
         text-decoration: none;
      }
   }
`;

const InnerWrapper = styled.div`
   margin-top: 10px;

   ${devices.desktopHD} {
      margin: 0;
   }
`;

export default NewsView;
